import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOSchema from '../../components/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://goautomat.com';

export default function Privacy() {
  useEffect(() => {
    document.title = 'Política de Privacidade | Automat';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Política de Privacidade da Automat. Saiba como coletamos, usamos e protegemos seus dados pessoais em conformidade com a LGPD.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'política de privacidade, LGPD, proteção de dados, privacidade automat');
    }

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Política de Privacidade",
    "description": "Política de Privacidade da Automat. Saiba como coletamos, usamos e protegemos seus dados pessoais.",
    "url": `${siteUrl}/privacidade`
  };

  return (
    <div className="bg-gray-950 min-h-screen">
      <SEOSchema schema={privacySchema} />

      {/* Navigation */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center cursor-pointer">
              <img src="/assets/media/automat-logo.png" alt="Automat Logo" className="h-10 w-auto" />
            </Link>
            <Link to="/" className="text-gray-400 hover:text-purple-400 transition-colors duration-300 cursor-pointer">
              Voltar ao Início
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          POLÍTICA DE PRIVACIDADE — AUTOMAT
        </h1>

        <p className="text-gray-400 mb-4">Última atualização: 11 de junho de 2026</p>

        <p className="text-gray-500 text-sm mb-12 italic">
          Versão em inglês disponível em{' '}
          <Link to="/privacy" className="text-purple-400 hover:text-purple-300">
            goautomat.com/privacy
          </Link>
          .
        </p>

        <div className="space-y-8 text-gray-300">
          <section>
            <p className="mb-6">
              A AUTOMAT TECNOLOGIA DA INFORMAÇÃO LTDA (doravante "AUTOMAT") compromete-se com a segurança e a privacidade no tratamento de dados. Esta Política descreve como operamos nossa plataforma de atendimento (SaaS) em conformidade com a Lei Geral de Proteção de Dados (LGPD) e com os termos da Plataforma do WhatsApp Business, fornecida pela Meta Platforms, Inc.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. DEFINIÇÕES E PAPÉIS</h2>
            <div className="space-y-3">
              <p>
                <strong className="text-white">Você (CONTRATANTE):</strong> É o Controlador dos dados. Você detém a responsabilidade sobre quais dados de seus clientes finais coleta e insere na plataforma.
              </p>
              <p>
                <strong className="text-white">AUTOMAT:</strong> Atuamos como Operadora. Fornecemos a tecnologia para processar essas informações segundo suas instruções.
              </p>
              <p>
                <strong className="text-white">Meta Platforms, Inc.:</strong> Fornecedora da Plataforma do WhatsApp Business (WhatsApp Business Platform / Cloud API), por meio da qual as mensagens são transmitidas. A Meta atua como sub-operadora no fluxo de mensagens do WhatsApp e trata os dados conforme suas próprias políticas.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. INFORMAÇÕES QUE PROCESSAMOS</h2>
            <p className="mb-4">Para o funcionamento do serviço, processamos:</p>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-3">A. Dados da Sua Conta:</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Dados cadastrais (Razão Social, Nome, e-mail, telefone, CNPJ/CPF) para faturamento, emissão de nota fiscal e acesso ao sistema.</li>
                <li>Logs de acesso e dados técnicos de conexão para segurança da conta.</li>
              </ul>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-semibold text-white mb-3">B. Dados de Terceiros (Seus Clientes/Leads):</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Histórico de mensagens de texto, áudio, imagens e arquivos trocados através da plataforma.</li>
                <li>Dados de perfil público do WhatsApp (nome e foto) dos usuários que interagem com seu número conectado.</li>
                <li>Número de telefone e metadados de conversa (data, horário e status de entrega das mensagens) dos contatos que interagem com seu número.</li>
                <li>
                  <strong className="text-white">Histórico de conversas importado:</strong> ao conectar seu número no modo Coexistence, a Plataforma do WhatsApp Business pode disponibilizar até 6 (seis) meses de histórico de mensagens anteriores, que é importado para a plataforma para dar continuidade ao atendimento.
                </li>
                <li>
                  <strong className="text-white">Mensagens enviadas pelo CONTRATANTE pelo aplicativo:</strong> no modo Coexistence, mensagens que você ou sua equipe enviam diretamente pelo aplicativo WhatsApp Business em seus dispositivos são sincronizadas com a plataforma (por meio dos eventos de eco fornecidos pela Meta), para manter a caixa de entrada unificada e atualizada.
                </li>
                <li>Informações inseridas manualmente por sua equipe no CRM (etiquetas, notas, funis).</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. DA INTEGRAÇÃO E USO DAS INFORMAÇÕES</h2>
            <p className="mb-4">
              A AUTOMAT integra-se à <strong className="text-white">Plataforma do WhatsApp Business (WhatsApp Business Platform / Cloud API)</strong>, fornecida pela Meta Platforms, Inc., atuando como <strong className="text-white">Provedor de Tecnologia (Tech Provider)</strong> autorizado.
            </p>
            <p className="mb-4">
              A conexão do número do CONTRATANTE é realizada exclusivamente por meio do fluxo oficial de <strong className="text-white">Embedded Signup</strong>, incluindo a modalidade <strong className="text-white">Coexistence</strong>, na qual o CONTRATANTE conecta seu número à plataforma mantendo, simultaneamente, o uso do aplicativo WhatsApp Business em seu dispositivo. <strong className="text-white">Não utilizamos métodos não oficiais de acesso, emulação, espelhamento ou automação não autorizada do aplicativo WhatsApp.</strong>
            </p>
            <p className="mb-4">Utilizamos as informações estritamente para:</p>
            <ul className="list-disc list-inside space-y-3 ml-4">
              <li>
                <strong className="text-white">Conectividade:</strong> estabelecer e manter a conexão entre o número do CONTRATANTE e a plataforma de gestão por meio da API oficial da Meta.
              </li>
              <li>
                <strong className="text-white">Atendimento:</strong> centralizar e organizar as conversas em uma caixa de entrada unificada, permitindo que sua equipe responda e gerencie os atendimentos.
              </li>
              <li>
                <strong className="text-white">Automação e Inteligência Artificial:</strong> processar as mensagens recebidas e gerar respostas automáticas, inclusive por meio de Inteligência Artificial, conforme os fluxos configurados pelo CONTRATANTE. Para isso, o conteúdo de mensagens pode ser transmitido a provedores de modelos de IA contratados pela AUTOMAT, exclusivamente para gerar a resposta ou executar a automação solicitada, sob obrigações de confidencialidade e sem que tais provedores utilizem o conteúdo para treinar seus modelos.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. COMPARTILHAMENTO E ARMAZENAMENTO</h2>
            <ul className="list-disc list-inside space-y-3 ml-4 mb-4">
              <li>Os dados ficam armazenados em servidores seguros, com criptografia em trânsito e em repouso.</li>
              <li><strong className="text-white">Não vendemos, alugamos ou compartilhamos seus dados com terceiros para fins comerciais ou publicitários.</strong></li>
              <li>
                Podemos compartilhar informações apenas com prestadores de serviço (sub-operadores) essenciais à prestação do serviço, sob rigorosos acordos de confidencialidade, incluindo:
                <ul className="list-disc list-inside space-y-2 ml-6 mt-2">
                  <li><strong className="text-white">Meta Platforms, Inc.</strong> — transmissão das mensagens via WhatsApp Business Platform;</li>
                  <li><strong className="text-white">Supabase</strong> — banco de dados e infraestrutura de armazenamento;</li>
                  <li><strong className="text-white">Vercel</strong> — hospedagem da aplicação;</li>
                  <li><strong className="text-white">Provedores de Inteligência Artificial</strong> — processamento de mensagens para geração de respostas automáticas, quando habilitado pelo CONTRATANTE.</li>
                </ul>
              </li>
              <li>O tratamento de dados realizado através da Plataforma do WhatsApp Business observa os Termos da Meta e as políticas aplicáveis da plataforma.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. SEGURANÇA E PROTEÇÃO</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Implementamos medidas técnicas e organizacionais para proteger os dados contra acessos não autorizados, vazamentos ou perdas.</li>
              <li>Utilizamos autenticação, criptografia SSL/TLS, validação de integridade das requisições recebidas (assinatura) e monitoramento contínuo de segurança.</li>
              <li>Realizamos backups regulares para garantir a continuidade do serviço.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. SEUS DIREITOS (LGPD)</h2>
            <p className="mb-4">
              Como Controlador dos dados de seus clientes, você é responsável por garantir os direitos deles. Como titular dos dados da sua conta na AUTOMAT, você pode:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Acessar, corrigir ou excluir seus dados cadastrais.</li>
              <li>Solicitar a portabilidade dos dados.</li>
              <li>Revogar consentimentos a qualquer momento.</li>
              <li>Solicitar a exclusão completa da conta e dos dados associados.</li>
            </ul>
            <p>
              Para exercer esses direitos, entre em contato através do e-mail:{' '}
              <strong className="text-white">contato@goautomat.com</strong>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. RETENÇÃO DE DADOS</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Mantemos os dados pelo período de vigência do contrato.</li>
              <li>Após o cancelamento, os dados podem ser retidos por até 5 (cinco) anos para fins de auditoria, conformidade legal e resolução de disputas.</li>
              <li>Você pode solicitar a exclusão antecipada, salvo quando houver obrigação legal de retenção.</li>
              <li>Mediante solicitação de exclusão, os dados de mensagens e contatos associados à conta são removidos de nossos sistemas ativos dentro de prazo razoável, observadas as obrigações legais de retenção.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. COOKIES E RASTREAMENTO</h2>
            <p>
              Utilizamos cookies essenciais para o funcionamento da plataforma (autenticação, sessão) e cookies analíticos para melhorar a experiência do usuário. Você pode gerenciar suas preferências de cookies nas configurações do navegador.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. ALTERAÇÕES NESTA POLÍTICA</h2>
            <p>
              Podemos atualizar esta Política periodicamente. Mudanças significativas serão comunicadas por e-mail ou através de aviso na plataforma. A data da última atualização será sempre indicada no topo deste documento.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. RESPONSABILIDADE DO CONTRATANTE</h2>
            <p className="mb-4">Como Controlador, você é responsável por:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Obter consentimento válido de seus clientes finais para coleta e tratamento de dados.</li>
              <li>Informar seus clientes sobre como os dados serão utilizados.</li>
              <li>Garantir que apenas dados necessários sejam inseridos na plataforma.</li>
              <li>Atender solicitações de direitos dos titulares (seus clientes).</li>
              <li>Utilizar a plataforma em conformidade com os Termos da Meta e as políticas do WhatsApp Business, incluindo as políticas de mensagens e de uso aceitável.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. LEGISLAÇÃO APLICÁVEL</h2>
            <p className="mb-4">Esta Política é regida pela legislação brasileira, especialmente:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Lei nº 13.709/2018 (LGPD)</li>
              <li>Marco Civil da Internet (Lei nº 12.965/2014)</li>
              <li>Código de Defesa do Consumidor (Lei nº 8.078/1990)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. CONTATO E ENCARREGADO DE DADOS</h2>
            <p className="mb-4">Para dúvidas, solicitações ou exercício de direitos relacionados à privacidade:</p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <ul className="list-none space-y-3">
                <li><strong className="text-white">Razão Social:</strong> AUTOMAT TECNOLOGIA DA INFORMAÇÃO LTDA</li>
                <li><strong className="text-white">CNPJ:</strong> 59.640.197/0001-76</li>
                <li><strong className="text-white">E-mail:</strong> contato@goautomat.com</li>
                <li><strong className="text-white">Website:</strong> goautomat.com</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. CONSENTIMENTO</h2>
            <p>
              Ao utilizar a plataforma AUTOMAT, você declara ter lido, compreendido e concordado com os termos desta Política de Privacidade.
            </p>
          </section>

          <div className="pt-8 border-t border-gray-800 text-sm text-gray-500">
            <p className="mb-2">Última atualização: 11 de junho de 2026. Documento válido e vigente a partir desta data.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>© 2026 Automat. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
